/**
 * Message Component — Phase 1 MVP
 * FootballGirl Super-App
 *
 * Features:
 *  - Firebase Auth integration (user identity)
 *  - Firestore real-time listeners (onSnapshot = WebSocket-like sync)
 *  - 1-on-1 private chat
 *  - Message history (persisted in Firestore)
 *  - Online / Away status (heartbeat written to Firestore)
 *  - User search (by displayName / email prefix)
 *  - User invite (creates a conversation doc)
 *  - Image sending (Firebase Storage upload → download URL stored in message)
 *  - Voice notes (MediaRecorder → Storage → playback)
 *  - Voice calling (Agora RTC, same SDK already in the project)
 */

import {
    getFirestore,
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    addDoc,
    updateDoc,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    serverTimestamp,
    arrayUnion,
} from 'firebase/firestore';
import {
    getStorage,
    ref as storageRef,
    uploadBytes,
    getDownloadURL,
} from 'firebase/storage';
import { getAuth } from 'firebase/auth';

// ─── Agora (already a project dep via Stream.js) ──────────────────────────────
// We import lazily to avoid blocking if the SDK isn't bundled for this route.
let AgoraRTC = null;
async function getAgora() {
    if (!AgoraRTC) {
        AgoraRTC = (await import('agora-rtc-sdk-ng')).default;
    }
    return AgoraRTC;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const AGORA_APP_ID = import.meta.env.VITE_AGORA_APP_ID ?? '';
const HEARTBEAT_INTERVAL = 30_000; // 30 s
const ONLINE_THRESHOLD = 60_000;   // consider online if heartbeat < 60 s ago
const MAX_IMAGE_MB = 10;
const MAX_VOICE_MINUTES = 5;

export class Message {
    constructor(containerId) {
        this.containerId = containerId;
        this.db = getFirestore();
        this.storage = getStorage();
        this.auth = getAuth();

        // State
        this.currentUser = null;
        this.activeConvId = null;
        this.conversations = [];
        this.messages = [];
        this.searchResults = [];

        // Cleanup handles
        this._unsubConversations = null;
        this._unsubMessages = null;
        this._heartbeatTimer = null;
        this._agoraClient = null;
        this._localAudioTrack = null;
        this._mediaRecorder = null;
        this._voiceChunks = [];
        this._isRecording = false;
        this._inCall = false;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // INIT
    // ═══════════════════════════════════════════════════════════════════════════

    async init() {
        const container = document.getElementById(this.containerId);
        if (!container) return console.error(`Message: #${this.containerId} not found`);

        this.currentUser = this.auth.currentUser;
        if (!this.currentUser) {
            container.innerHTML = `<div class="msg-auth-wall">Please sign in to use messages.</div>`;
            return;
        }

        this._injectStyles();
        container.innerHTML = this._buildShell();
        this._bindShellEvents();
        this._startHeartbeat();
        this._subscribeConversations();
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // LAYOUT SHELL
    // ═══════════════════════════════════════════════════════════════════════════

    _buildShell() {
        return `
        <div class="msg-root" id="msg-root">

          <!-- ── LEFT PANEL ── -->
          <aside class="msg-sidebar" id="msg-sidebar">
            <header class="msg-sidebar-header">
              <div class="msg-logo-row">
                <span class="msg-logo-icon">⚽</span>
                <span class="msg-logo-text">Messages</span>
              </div>
              <button class="msg-icon-btn" id="msg-new-chat-btn" title="New conversation">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M12 5v14M5 12h14"/></svg>
              </button>
            </header>

            <!-- Search bar -->
            <div class="msg-search-wrap">
              <svg class="msg-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input class="msg-search-input" id="msg-search-input" placeholder="Search people…" autocomplete="off"/>
              <div class="msg-search-results" id="msg-search-results" style="display:none"></div>
            </div>

            <!-- Conversation list -->
            <div class="msg-conv-list" id="msg-conv-list">
              <div class="msg-empty-state">
                <span class="msg-empty-icon">💬</span>
                <p>No conversations yet.<br>Search for someone to start chatting!</p>
              </div>
            </div>
          </aside>

          <!-- ── RIGHT PANEL ── -->
          <main class="msg-main" id="msg-main">
            <div class="msg-welcome" id="msg-welcome">
              <div class="msg-welcome-inner">
                <span class="msg-welcome-emoji">⚽</span>
                <h2>FootballGirl Messages</h2>
                <p>Select a conversation or search for a teammate to start chatting.</p>
              </div>
            </div>

            <!-- Chat view (hidden until conversation selected) -->
            <div class="msg-chat-view" id="msg-chat-view" style="display:none">

              <!-- Chat header -->
              <header class="msg-chat-header" id="msg-chat-header">
                <button class="msg-icon-btn msg-back-btn" id="msg-back-btn" title="Back">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
                </button>
                <div class="msg-chat-header-avatar" id="msg-chat-header-avatar"></div>
                <div class="msg-chat-header-info">
                  <div class="msg-chat-header-name" id="msg-chat-header-name">—</div>
                  <div class="msg-chat-header-status" id="msg-chat-header-status"></div>
                </div>
                <div class="msg-chat-header-actions">
                  <button class="msg-icon-btn" id="msg-call-btn" title="Voice call">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.22 1.18 2 2 0 012.2 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                  </button>
                </div>
              </header>

              <!-- Messages area -->
              <div class="msg-messages-area" id="msg-messages-area"></div>

              <!-- Typing indicator -->
              <div class="msg-typing-indicator" id="msg-typing-indicator" style="display:none">
                <span></span><span></span><span></span>
              </div>

              <!-- Composer -->
              <footer class="msg-composer" id="msg-composer">
                <!-- Image attach -->
                <label class="msg-icon-btn msg-attach-btn" title="Send image">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  <input type="file" id="msg-image-input" accept="image/*" style="display:none"/>
                </label>
                <!-- Voice note -->
                <button class="msg-icon-btn msg-voice-btn" id="msg-voice-btn" title="Voice note">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                </button>
                <input class="msg-text-input" id="msg-text-input" placeholder="Message…" autocomplete="off"/>
                <button class="msg-send-btn" id="msg-send-btn" title="Send">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </button>
              </footer>

              <!-- Voice recording UI -->
              <div class="msg-recording-bar" id="msg-recording-bar" style="display:none">
                <div class="msg-recording-pulse"></div>
                <span id="msg-recording-timer">0:00</span>
                <button class="msg-recording-cancel" id="msg-recording-cancel">Cancel</button>
                <button class="msg-recording-send" id="msg-recording-send">Send</button>
              </div>
            </div>

            <!-- In-call overlay -->
            <div class="msg-call-overlay" id="msg-call-overlay" style="display:none">
              <div class="msg-call-card">
                <div class="msg-call-avatar" id="msg-call-avatar"></div>
                <div class="msg-call-name" id="msg-call-name"></div>
                <div class="msg-call-status" id="msg-call-status">Connecting…</div>
                <div class="msg-call-actions">
                  <button class="msg-call-mute" id="msg-call-mute" title="Mute">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/></svg>
                  </button>
                  <button class="msg-call-end" id="msg-call-end" title="End call">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.22 1.18 2 2 0 012.2 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
        `;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // EVENT BINDING
    // ═══════════════════════════════════════════════════════════════════════════

    _bindShellEvents() {
        // New chat button
        document.getElementById('msg-new-chat-btn')?.addEventListener('click', () => {
            document.getElementById('msg-search-input').focus();
        });

        // Back button (mobile)
        document.getElementById('msg-back-btn')?.addEventListener('click', () => {
            this._showWelcome();
        });

        // Search input
        const searchInput = document.getElementById('msg-search-input');
        let searchDebounce;
        searchInput?.addEventListener('input', (e) => {
            clearTimeout(searchDebounce);
            const q = e.target.value.trim();
            if (q.length < 2) {
                this._hideSearchResults();
                return;
            }
            searchDebounce = setTimeout(() => this._searchUsers(q), 350);
        });
        searchInput?.addEventListener('blur', () => {
            setTimeout(() => this._hideSearchResults(), 200);
        });

        // Text input — send on Enter
        document.getElementById('msg-text-input')?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this._sendTextMessage();
            }
        });

        // Send button
        document.getElementById('msg-send-btn')?.addEventListener('click', () => {
            this._sendTextMessage();
        });

        // Image input
        document.getElementById('msg-image-input')?.addEventListener('change', (e) => {
            const file = e.target.files?.[0];
            if (file) this._sendImageMessage(file);
            e.target.value = '';
        });

        // Voice note button
        document.getElementById('msg-voice-btn')?.addEventListener('click', () => {
            this._startVoiceRecording();
        });

        // Recording controls
        document.getElementById('msg-recording-cancel')?.addEventListener('click', () => {
            this._cancelRecording();
        });
        document.getElementById('msg-recording-send')?.addEventListener('click', () => {
            this._stopAndSendVoiceNote();
        });

        // Voice call button
        document.getElementById('msg-call-btn')?.addEventListener('click', () => {
            this._startVoiceCall();
        });

        // Call controls
        document.getElementById('msg-call-mute')?.addEventListener('click', () => {
            this._toggleMute();
        });
        document.getElementById('msg-call-end')?.addEventListener('click', () => {
            this._endCall();
        });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // FIRESTORE — CONVERSATIONS
    // ═══════════════════════════════════════════════════════════════════════════

    _subscribeConversations() {
        const uid = this.currentUser.uid;
        const q = query(
            collection(this.db, 'conversations'),
            where('participants', 'array-contains', uid),
            orderBy('updatedAt', 'desc'),
            limit(50)
        );

        this._unsubConversations = onSnapshot(q, (snap) => {
            this.conversations = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            this._renderConvList();
        });
    }

    _renderConvList() {
        const list = document.getElementById('msg-conv-list');
        if (!list) return;

        if (this.conversations.length === 0) {
            list.innerHTML = `
                <div class="msg-empty-state">
                  <span class="msg-empty-icon">💬</span>
                  <p>No conversations yet.<br>Search for a teammate!</p>
                </div>`;
            return;
        }

        list.innerHTML = this.conversations.map(conv => {
            const otherId = conv.participants.find(p => p !== this.currentUser.uid);
            const other = conv.participantData?.[otherId] ?? {};
            const isActive = conv.id === this.activeConvId;
            const online = this._isOnline(conv.onlineStatus?.[otherId]);
            const initials = this._initials(other.displayName ?? other.email ?? '?');
            const preview = this._truncate(conv.lastMessage ?? '', 38);
            const time = this._formatTime(conv.updatedAt?.toDate?.() ?? new Date());
            const unread = conv.unread?.[this.currentUser.uid] ?? 0;

            return `
            <div class="msg-conv-item ${isActive ? 'msg-conv-item--active' : ''}"
                 data-conv-id="${conv.id}" data-other-id="${otherId}">
              <div class="msg-avatar-wrap">
                <div class="msg-avatar" style="background:${this._avatarColor(otherId)}">
                  ${other.photoURL
                    ? `<img src="${other.photoURL}" alt="${initials}"/>`
                    : initials}
                </div>
                <div class="msg-status-dot ${online ? 'msg-status-dot--online' : ''}"></div>
              </div>
              <div class="msg-conv-info">
                <div class="msg-conv-name">${other.displayName ?? other.email ?? 'Unknown'}</div>
                <div class="msg-conv-preview">${preview}</div>
              </div>
              <div class="msg-conv-meta">
                <div class="msg-conv-time">${time}</div>
                ${unread > 0 ? `<div class="msg-unread-badge">${unread}</div>` : ''}
              </div>
            </div>`;
        }).join('');

        // Click handlers
        list.querySelectorAll('.msg-conv-item').forEach(el => {
            el.addEventListener('click', () => {
                const convId = el.dataset.convId;
                const otherId = el.dataset.otherId;
                this._openConversation(convId, otherId);
            });
        });
    }

    async _openConversation(convId, otherUid) {
        this.activeConvId = convId;
        this._renderConvList(); // highlight

        // Load other user info
        let otherUser = {};
        try {
            const convData = this.conversations.find(c => c.id === convId);
            otherUser = convData?.participantData?.[otherUid] ?? {};
        } catch (_) {}

        // Update header
        document.getElementById('msg-chat-header-name').textContent =
            otherUser.displayName ?? otherUser.email ?? 'User';
        const avatarEl = document.getElementById('msg-chat-header-avatar');
        const initials = this._initials(otherUser.displayName ?? otherUser.email ?? '?');
        avatarEl.innerHTML = otherUser.photoURL
            ? `<img src="${otherUser.photoURL}" class="msg-avatar-img"/>`
            : `<div class="msg-avatar" style="background:${this._avatarColor(otherUid)}">${initials}</div>`;

        // Update status
        const convData = this.conversations.find(c => c.id === convId);
        const online = this._isOnline(convData?.onlineStatus?.[otherUid]);
        document.getElementById('msg-chat-header-status').textContent = online ? '🟢 Active now' : '⚫ Away';

        // Store call target
        this._callTargetUid = otherUid;
        this._callTargetName = otherUser.displayName ?? otherUser.email ?? 'User';

        // Mark unread
        await this._markRead(convId);

        // Show chat view
        document.getElementById('msg-welcome').style.display = 'none';
        document.getElementById('msg-chat-view').style.display = 'flex';

        // Subscribe to messages
        this._subscribeMessages(convId);
    }

    _subscribeMessages(convId) {
        if (this._unsubMessages) this._unsubMessages();

        const q = query(
            collection(this.db, 'conversations', convId, 'messages'),
            orderBy('createdAt', 'asc'),
            limit(200)
        );

        this._unsubMessages = onSnapshot(q, (snap) => {
            this.messages = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            this._renderMessages();
        });
    }

    _renderMessages() {
        const area = document.getElementById('msg-messages-area');
        if (!area) return;

        const uid = this.currentUser.uid;
        const html = this.messages.map((msg, i) => {
            const isMine = msg.senderId === uid;
            const prev = this.messages[i - 1];
            const showAvatar = !isMine && prev?.senderId !== msg.senderId;
            const time = this._formatTime(msg.createdAt?.toDate?.() ?? new Date());

            let content = '';
            if (msg.type === 'image') {
                content = `<img src="${msg.imageUrl}" class="msg-bubble-img" alt="Image" loading="lazy" onclick="this.requestFullscreen?.()"/>`;
            } else if (msg.type === 'voice') {
                content = `
                <div class="msg-voice-player">
                  <button class="msg-voice-play-btn" onclick="
                    const a = this.parentElement.querySelector('audio');
                    a.paused ? (a.play(), this.textContent='⏸') : (a.pause(), this.textContent='▶')
                  ">▶</button>
                  <div class="msg-voice-waveform">
                    ${Array.from({length: 20}, () =>
                        `<div class="msg-voice-bar" style="height:${4 + Math.random()*16}px"></div>`
                    ).join('')}
                  </div>
                  <span class="msg-voice-duration">${msg.duration ?? '—'}</span>
                  <audio src="${msg.audioUrl}" preload="none" style="display:none"></audio>
                </div>`;
            } else {
                content = `<span>${this._escapeHtml(msg.text ?? '')}</span>`;
            }

            return `
            <div class="msg-message-row ${isMine ? 'msg-message-row--mine' : 'msg-message-row--theirs'}">
              ${showAvatar ? `<div class="msg-avatar msg-avatar--sm" style="background:${this._avatarColor(msg.senderId)}">${this._initials(msg.senderName ?? '')}</div>` : '<div class="msg-avatar-spacer"></div>'}
              <div class="msg-bubble ${isMine ? 'msg-bubble--mine' : 'msg-bubble--theirs'}">
                ${content}
                <div class="msg-bubble-time">${time}</div>
              </div>
            </div>`;
        }).join('');

        area.innerHTML = html || `<div class="msg-empty-state" style="margin-top:3rem"><span class="msg-empty-icon">✉️</span><p>No messages yet. Say hello!</p></div>`;
        area.scrollTop = area.scrollHeight;
    }

    _showWelcome() {
        this.activeConvId = null;
        if (this._unsubMessages) this._unsubMessages();
        document.getElementById('msg-welcome').style.display = 'flex';
        document.getElementById('msg-chat-view').style.display = 'none';
        this._renderConvList();
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SENDING MESSAGES
    // ═══════════════════════════════════════════════════════════════════════════

    async _sendTextMessage() {
        const input = document.getElementById('msg-text-input');
        const text = input?.value.trim();
        if (!text || !this.activeConvId) return;
        input.value = '';
        await this._writeMessage({ type: 'text', text });
    }

    async _sendImageMessage(file) {
        if (!this.activeConvId) return;
        if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
            alert(`Images must be under ${MAX_IMAGE_MB}MB.`);
            return;
        }

        // Optimistic UI: show uploading bubble
        const area = document.getElementById('msg-messages-area');
        const tmpId = `tmp-${Date.now()}`;
        const tmpEl = document.createElement('div');
        tmpEl.id = tmpId;
        tmpEl.className = 'msg-message-row msg-message-row--mine';
        tmpEl.innerHTML = `<div class="msg-bubble msg-bubble--mine msg-bubble--uploading">📎 Uploading image…</div>`;
        area.appendChild(tmpEl);
        area.scrollTop = area.scrollHeight;

        const path = `messages/${this.activeConvId}/${Date.now()}_${file.name}`;
        const sRef = storageRef(this.storage, path);
        await uploadBytes(sRef, file);
        const imageUrl = await getDownloadURL(sRef);

        tmpEl.remove();
        await this._writeMessage({ type: 'image', imageUrl, text: '📷 Image' });
    }

    async _writeMessage(payload) {
        const uid = this.currentUser.uid;
        const convRef = doc(this.db, 'conversations', this.activeConvId);
        const msgRef = collection(this.db, 'conversations', this.activeConvId, 'messages');

        const msgDoc = {
            senderId: uid,
            senderName: this.currentUser.displayName ?? this.currentUser.email ?? 'User',
            createdAt: serverTimestamp(),
            ...payload,
        };
        await addDoc(msgRef, msgDoc);

        // Update conversation metadata
        const conv = this.conversations.find(c => c.id === this.activeConvId);
        const otherId = conv?.participants.find(p => p !== uid);
        await updateDoc(convRef, {
            lastMessage: payload.text ?? payload.type,
            updatedAt: serverTimestamp(),
            [`unread.${otherId}`]: (conv?.unread?.[otherId] ?? 0) + 1,
        });
    }

    async _markRead(convId) {
        const uid = this.currentUser.uid;
        await updateDoc(doc(this.db, 'conversations', convId), {
            [`unread.${uid}`]: 0,
        });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // VOICE NOTES
    // ═══════════════════════════════════════════════════════════════════════════

    async _startVoiceRecording() {
        if (this._isRecording || !this.activeConvId) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this._voiceChunks = [];
            this._mediaRecorder = new MediaRecorder(stream);
            this._mediaRecorder.ondataavailable = e => this._voiceChunks.push(e.data);

            this._mediaRecorder.start();
            this._isRecording = true;

            // Show recording bar
            document.getElementById('msg-composer').style.display = 'none';
            document.getElementById('msg-recording-bar').style.display = 'flex';

            // Timer
            let secs = 0;
            this._recordingTimer = setInterval(() => {
                secs++;
                const m = Math.floor(secs / 60);
                const s = String(secs % 60).padStart(2, '0');
                document.getElementById('msg-recording-timer').textContent = `${m}:${s}`;
                if (secs >= MAX_VOICE_MINUTES * 60) this._stopAndSendVoiceNote();
            }, 1000);

        } catch (err) {
            console.error('Mic error:', err);
            alert('Could not access microphone. Please check your browser permissions.');
        }
    }

    _cancelRecording() {
        if (!this._isRecording) return;
        this._mediaRecorder?.stop();
        this._mediaRecorder?.stream.getTracks().forEach(t => t.stop());
        clearInterval(this._recordingTimer);
        this._isRecording = false;
        this._voiceChunks = [];
        document.getElementById('msg-recording-bar').style.display = 'none';
        document.getElementById('msg-composer').style.display = 'flex';
    }

    async _stopAndSendVoiceNote() {
        if (!this._isRecording) return;
        clearInterval(this._recordingTimer);
        const timerText = document.getElementById('msg-recording-timer').textContent;

        await new Promise(res => {
            this._mediaRecorder.onstop = res;
            this._mediaRecorder.stop();
            this._mediaRecorder.stream.getTracks().forEach(t => t.stop());
        });

        this._isRecording = false;
        document.getElementById('msg-recording-bar').style.display = 'none';
        document.getElementById('msg-composer').style.display = 'flex';

        const blob = new Blob(this._voiceChunks, { type: 'audio/webm' });
        const path = `voices/${this.activeConvId}/${Date.now()}.webm`;
        const sRef = storageRef(this.storage, path);
        await uploadBytes(sRef, blob);
        const audioUrl = await getDownloadURL(sRef);

        await this._writeMessage({ type: 'voice', audioUrl, duration: timerText, text: '🎤 Voice note' });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // VOICE CALLING (Agora)
    // ═══════════════════════════════════════════════════════════════════════════

    async _startVoiceCall() {
        if (this._inCall || !this.activeConvId || !AGORA_APP_ID) {
            if (!AGORA_APP_ID) alert('Voice calling requires VITE_AGORA_APP_ID in your .env file.');
            return;
        }
        try {
            const Agora = await getAgora();
            this._agoraClient = Agora.createClient({ mode: 'rtc', codec: 'vp8' });
            const channel = `chat_${this.activeConvId}`;
            const uid = this.currentUser.uid;

            // NOTE: In production, fetch a real token from your Cloud Function
            // e.g. const { token } = await fetch('/api/agora-token?channel='+channel).then(r=>r.json())
            await this._agoraClient.join(AGORA_APP_ID, channel, null, uid);

            this._localAudioTrack = await Agora.createMicrophoneAudioTrack();
            await this._agoraClient.publish([this._localAudioTrack]);

            this._inCall = true;
            this._showCallOverlay(this._callTargetName);

            // Remote user subscription
            this._agoraClient.on('user-published', async (user, mediaType) => {
                await this._agoraClient.subscribe(user, mediaType);
                if (mediaType === 'audio') {
                    user.audioTrack.play();
                    document.getElementById('msg-call-status').textContent = 'Connected';
                }
            });

            // Write a call-initiated message
            await this._writeMessage({ type: 'text', text: '📞 Started a voice call' });

        } catch (err) {
            console.error('Agora call error:', err);
            alert('Could not start voice call: ' + err.message);
        }
    }

    _showCallOverlay(name) {
        const overlay = document.getElementById('msg-call-overlay');
        document.getElementById('msg-call-name').textContent = name;
        document.getElementById('msg-call-avatar').textContent = this._initials(name);
        document.getElementById('msg-call-status').textContent = 'Connecting…';
        overlay.style.display = 'flex';
    }

    _toggleMute() {
        if (!this._localAudioTrack) return;
        const muted = this._localAudioTrack.enabled === false;
        this._localAudioTrack.setEnabled(muted);
        document.getElementById('msg-call-mute').style.opacity = muted ? '1' : '0.5';
    }

    async _endCall() {
        if (this._localAudioTrack) {
            this._localAudioTrack.stop();
            this._localAudioTrack.close();
        }
        await this._agoraClient?.leave();
        this._agoraClient = null;
        this._inCall = false;
        document.getElementById('msg-call-overlay').style.display = 'none';
        await this._writeMessage({ type: 'text', text: '📞 Call ended' });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // USER SEARCH + INVITE
    // ═══════════════════════════════════════════════════════════════════════════

    async _searchUsers(queryStr) {
        const q = queryStr.toLowerCase();
        try {
            // Search by displayName (case-insensitive prefix via Firestore range query)
            const usersRef = collection(this.db, 'users');
            const nameQuery = query(
                usersRef,
                where('displayNameLower', '>=', q),
                where('displayNameLower', '<=', q + '\uf8ff'),
                limit(10)
            );
            const snap = await getDocs(nameQuery);
            const results = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(u => u.id !== this.currentUser.uid);

            this._showSearchResults(results, queryStr);
        } catch (err) {
            console.error('Search error:', err);
        }
    }

    _showSearchResults(users, query) {
        const el = document.getElementById('msg-search-results');
        if (!el) return;

        if (users.length === 0) {
            el.innerHTML = `<div class="msg-search-no-results">No users found for "${query}"</div>`;
            el.style.display = 'block';
            return;
        }

        el.innerHTML = users.map(u => `
            <div class="msg-search-result" data-uid="${u.id}">
              <div class="msg-avatar msg-avatar--sm" style="background:${this._avatarColor(u.id)}">
                ${u.photoURL ? `<img src="${u.photoURL}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;"/>` : this._initials(u.displayName ?? u.email ?? '?')}
              </div>
              <div class="msg-search-result-info">
                <div class="msg-search-result-name">${u.displayName ?? u.email ?? 'Unknown'}</div>
                <div class="msg-search-result-sub">${u.email ?? ''}</div>
              </div>
              <button class="msg-invite-btn" data-uid="${u.id}">Message</button>
            </div>`
        ).join('');

        el.querySelectorAll('.msg-invite-btn').forEach(btn => {
            btn.addEventListener('click', async () => {
                const uid = btn.dataset.uid;
                const user = users.find(u => u.id === uid);
                await this._inviteOrOpenConversation(user);
                this._hideSearchResults();
                document.getElementById('msg-search-input').value = '';
            });
        });

        el.style.display = 'block';
    }

    _hideSearchResults() {
        const el = document.getElementById('msg-search-results');
        if (el) el.style.display = 'none';
    }

    async _inviteOrOpenConversation(otherUser) {
        const uid = this.currentUser.uid;
        const otherId = otherUser.id;

        // Check if conversation already exists
        const existing = this.conversations.find(c =>
            c.participants.includes(uid) && c.participants.includes(otherId)
        );
        if (existing) {
            this._openConversation(existing.id, otherId);
            return;
        }

        // Create new conversation
        const me = this.currentUser;
        const convData = {
            participants: [uid, otherId],
            participantData: {
                [uid]: {
                    displayName: me.displayName ?? me.email,
                    email: me.email,
                    photoURL: me.photoURL ?? null,
                },
                [otherId]: {
                    displayName: otherUser.displayName ?? otherUser.email,
                    email: otherUser.email ?? '',
                    photoURL: otherUser.photoURL ?? null,
                },
            },
            lastMessage: '',
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            unread: { [uid]: 0, [otherId]: 0 },
            onlineStatus: {},
        };

        // Use deterministic ID so duplicates can't form
        const convId = [uid, otherId].sort().join('_');
        await setDoc(doc(this.db, 'conversations', convId), convData, { merge: true });
        this._openConversation(convId, otherId);
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // ONLINE STATUS HEARTBEAT
    // ═══════════════════════════════════════════════════════════════════════════

    _startHeartbeat() {
        const uid = this.currentUser.uid;
        const userRef = doc(this.db, 'users', uid);

        const ping = async () => {
            try {
                await updateDoc(userRef, {
                    lastSeen: serverTimestamp(),
                    online: true,
                });
            } catch (_) {
                // Doc may not exist yet — safe to ignore
            }
        };

        ping();
        this._heartbeatTimer = setInterval(ping, HEARTBEAT_INTERVAL);

        // Mark offline on page unload
        window.addEventListener('beforeunload', () => {
            navigator.sendBeacon?.(`/api/offline?uid=${uid}`);
            updateDoc(userRef, { online: false }).catch(() => {});
        });
    }

    _isOnline(lastSeenTimestamp) {
        if (!lastSeenTimestamp) return false;
        const ms = lastSeenTimestamp?.toMillis?.() ?? lastSeenTimestamp;
        return Date.now() - ms < ONLINE_THRESHOLD;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // CLEANUP
    // ═══════════════════════════════════════════════════════════════════════════

    destroy() {
        this._unsubConversations?.();
        this._unsubMessages?.();
        clearInterval(this._heartbeatTimer);
        clearInterval(this._recordingTimer);
        this._endCall().catch(() => {});
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // HELPERS
    // ═══════════════════════════════════════════════════════════════════════════

    _initials(name) {
        return name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
    }

    _truncate(str, len) {
        return str.length > len ? str.slice(0, len) + '…' : str;
    }

    _formatTime(date) {
        if (!(date instanceof Date) || isNaN(date)) return '';
        const now = new Date();
        const diff = now - date;
        if (diff < 60_000) return 'Just now';
        if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
        if (diff < 86_400_000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }

    _avatarColor(uid) {
        const colors = ['#FF6B6B','#4ECDC4','#45B7D1','#96CEB4','#FFEAA7','#DDA0DD','#98D8C8','#F7DC6F','#BB8FCE','#85C1E9'];
        let hash = 0;
        for (const c of String(uid)) hash = (hash * 31 + c.charCodeAt(0)) | 0;
        return colors[Math.abs(hash) % colors.length];
    }

    _escapeHtml(str) {
        return str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // STYLES
    // ═══════════════════════════════════════════════════════════════════════════

    _injectStyles() {
        if (document.getElementById('msg-styles')) return;
        const style = document.createElement('style');
        style.id = 'msg-styles';
        style.textContent = `
        /* ── Reset & Root ── */
        .msg-root {
          display: flex;
          height: calc(100vh - 64px); /* adjust 64px to your app header height */
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #f0f2f5;
          overflow: hidden;
          position: relative;
        }

        /* ── Sidebar ── */
        .msg-sidebar {
          width: 340px;
          min-width: 260px;
          background: #ffffff;
          border-right: 1px solid #e9eaed;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          overflow: hidden;
        }

        .msg-sidebar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 20px 14px;
          border-bottom: 1px solid #f0f2f5;
        }

        .msg-logo-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .msg-logo-icon { font-size: 1.4rem; }
        .msg-logo-text {
          font-size: 1.15rem;
          font-weight: 700;
          color: #0a0a0a;
          letter-spacing: -0.3px;
        }

        /* ── Search ── */
        .msg-search-wrap {
          position: relative;
          padding: 12px 16px;
          border-bottom: 1px solid #f0f2f5;
        }
        .msg-search-icon {
          position: absolute;
          left: 28px;
          top: 50%;
          transform: translateY(-50%);
          color: #65676b;
          pointer-events: none;
        }
        .msg-search-input {
          width: 100%;
          background: #f0f2f5;
          border: none;
          border-radius: 22px;
          padding: 9px 16px 9px 36px;
          font-size: 0.92rem;
          color: #050505;
          outline: none;
          transition: background 0.15s;
          box-sizing: border-box;
        }
        .msg-search-input::placeholder { color: #8a8d91; }
        .msg-search-input:focus { background: #e4e6eb; }

        .msg-search-results {
          position: absolute;
          left: 16px; right: 16px;
          top: calc(100% - 4px);
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
          z-index: 200;
          max-height: 320px;
          overflow-y: auto;
          border: 1px solid #e9eaed;
        }
        .msg-search-result {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .msg-search-result:hover { background: #f7f8fa; }
        .msg-search-result-name { font-weight: 600; font-size: 0.92rem; color: #050505; }
        .msg-search-result-sub { font-size: 0.78rem; color: #8a8d91; }
        .msg-search-no-results { padding: 16px; color: #8a8d91; font-size: 0.88rem; text-align: center; }
        .msg-invite-btn {
          margin-left: auto;
          background: #0a84ff;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 6px 14px;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.15s;
        }
        .msg-invite-btn:hover { background: #0070e0; }

        /* ── Conversation List ── */
        .msg-conv-list { flex: 1; overflow-y: auto; }
        .msg-conv-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          cursor: pointer;
          transition: background 0.15s;
          border-radius: 8px;
          margin: 2px 8px;
        }
        .msg-conv-item:hover { background: #f5f6f8; }
        .msg-conv-item--active { background: #e7f3ff !important; }

        .msg-avatar-wrap { position: relative; flex-shrink: 0; }
        .msg-avatar {
          width: 44px; height: 44px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 0.85rem; color: white;
          overflow: hidden; flex-shrink: 0;
        }
        .msg-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .msg-avatar--sm { width: 32px; height: 32px; font-size: 0.72rem; }
        .msg-avatar-spacer { width: 32px; flex-shrink: 0; }
        .msg-avatar-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }

        .msg-status-dot {
          position: absolute;
          bottom: 1px; right: 1px;
          width: 11px; height: 11px;
          border-radius: 50%;
          background: #ccc;
          border: 2px solid white;
        }
        .msg-status-dot--online { background: #31a24c; }

        .msg-conv-info { flex: 1; min-width: 0; }
        .msg-conv-name { font-weight: 600; font-size: 0.93rem; color: #050505; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .msg-conv-preview { font-size: 0.82rem; color: #65676b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
        .msg-conv-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
        .msg-conv-time { font-size: 0.75rem; color: #8a8d91; }
        .msg-unread-badge {
          background: #0a84ff; color: white;
          border-radius: 12px; padding: 1px 7px;
          font-size: 0.72rem; font-weight: 700;
          min-width: 18px; text-align: center;
        }

        /* ── Empty States ── */
        .msg-empty-state {
          display: flex; flex-direction: column; align-items: center;
          padding: 40px 20px; text-align: center; color: #8a8d91;
        }
        .msg-empty-icon { font-size: 2.5rem; margin-bottom: 10px; }
        .msg-empty-state p { font-size: 0.88rem; line-height: 1.5; }

        /* ── Main / Chat ── */
        .msg-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          position: relative;
          background: #f0f2f5;
        }

        .msg-welcome {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f2f5;
        }
        .msg-welcome-inner { text-align: center; color: #8a8d91; }
        .msg-welcome-emoji { font-size: 3.5rem; }
        .msg-welcome-inner h2 { font-size: 1.4rem; font-weight: 700; color: #050505; margin: 12px 0 8px; }
        .msg-welcome-inner p { font-size: 0.9rem; }

        /* Chat view */
        .msg-chat-view {
          flex: 1; display: flex; flex-direction: column; overflow: hidden;
        }

        /* Chat header */
        .msg-chat-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 18px;
          background: white;
          border-bottom: 1px solid #e9eaed;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          z-index: 10;
        }
        .msg-chat-header-info { flex: 1; }
        .msg-chat-header-name { font-weight: 700; font-size: 1rem; color: #050505; }
        .msg-chat-header-status { font-size: 0.78rem; color: #65676b; margin-top: 1px; }
        .msg-chat-header-actions { display: flex; gap: 8px; }
        .msg-back-btn { display: none; }

        /* Messages area */
        .msg-messages-area {
          flex: 1;
          overflow-y: auto;
          padding: 16px 20px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          scroll-behavior: smooth;
        }

        .msg-message-row {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          margin-bottom: 2px;
          animation: msgFadeIn 0.2s ease;
        }
        @keyframes msgFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .msg-message-row--mine { flex-direction: row-reverse; }
        .msg-message-row--theirs { flex-direction: row; }

        .msg-bubble {
          max-width: 68%;
          padding: 10px 14px;
          border-radius: 18px;
          font-size: 0.92rem;
          line-height: 1.45;
          word-break: break-word;
          position: relative;
        }
        .msg-bubble--mine {
          background: #0a84ff;
          color: white;
          border-bottom-right-radius: 4px;
        }
        .msg-bubble--theirs {
          background: white;
          color: #050505;
          border-bottom-left-radius: 4px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08);
        }
        .msg-bubble--uploading { opacity: 0.6; font-style: italic; }

        .msg-bubble-time {
          font-size: 0.68rem;
          margin-top: 4px;
          opacity: 0.65;
          text-align: right;
        }

        .msg-bubble-img {
          max-width: 240px;
          max-height: 220px;
          border-radius: 12px;
          display: block;
          cursor: zoom-in;
          object-fit: cover;
        }

        /* Voice player */
        .msg-voice-player {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 180px;
        }
        .msg-voice-play-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          color: inherit;
          border-radius: 50%;
          width: 32px; height: 32px;
          cursor: pointer;
          font-size: 1rem;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.15s;
        }
        .msg-voice-play-btn:hover { background: rgba(255,255,255,0.35); }
        .msg-voice-waveform { display: flex; align-items: center; gap: 2px; flex: 1; }
        .msg-voice-bar {
          width: 3px; border-radius: 2px;
          background: rgba(255,255,255,0.5);
          min-height: 4px;
        }
        .msg-bubble--theirs .msg-voice-bar { background: rgba(10,132,255,0.4); }
        .msg-voice-duration { font-size: 0.75rem; opacity: 0.8; white-space: nowrap; }

        /* Typing indicator */
        .msg-typing-indicator {
          padding: 8px 20px;
          display: flex;
          gap: 4px;
          align-items: center;
        }
        .msg-typing-indicator span {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #8a8d91;
          animation: typingBounce 1.2s infinite;
        }
        .msg-typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
        .msg-typing-indicator span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes typingBounce {
          0%,60%,100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }

        /* Composer */
        .msg-composer {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: white;
          border-top: 1px solid #e9eaed;
        }
        .msg-icon-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #65676b;
          padding: 8px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.15s, color 0.15s;
          flex-shrink: 0;
        }
        .msg-icon-btn:hover { background: #f0f2f5; color: #0a84ff; }
        .msg-attach-btn { cursor: pointer; }
        .msg-voice-btn.recording { color: #e74c3c; background: #ffe5e5; }

        .msg-text-input {
          flex: 1;
          background: #f0f2f5;
          border: none;
          border-radius: 22px;
          padding: 10px 18px;
          font-size: 0.93rem;
          color: #050505;
          outline: none;
          transition: background 0.15s;
        }
        .msg-text-input::placeholder { color: #8a8d91; }
        .msg-text-input:focus { background: #e4e6eb; }

        .msg-send-btn {
          width: 38px; height: 38px;
          border-radius: 50%;
          background: #0a84ff;
          color: white;
          border: none;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          transition: background 0.15s, transform 0.1s;
        }
        .msg-send-btn:hover { background: #0070e0; transform: scale(1.05); }
        .msg-send-btn:active { transform: scale(0.95); }

        /* Recording bar */
        .msg-recording-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: white;
          border-top: 1px solid #e9eaed;
        }
        .msg-recording-pulse {
          width: 12px; height: 12px;
          border-radius: 50%;
          background: #e74c3c;
          animation: recPulse 1s infinite;
          flex-shrink: 0;
        }
        @keyframes recPulse {
          0%,100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
        #msg-recording-timer { font-weight: 600; font-size: 0.95rem; color: #e74c3c; }
        .msg-recording-cancel, .msg-recording-send {
          border: none; border-radius: 8px; padding: 7px 14px;
          font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: background 0.15s;
        }
        .msg-recording-cancel { background: #f0f2f5; color: #65676b; margin-left: auto; }
        .msg-recording-cancel:hover { background: #e4e6eb; }
        .msg-recording-send { background: #0a84ff; color: white; }
        .msg-recording-send:hover { background: #0070e0; }

        /* Call overlay */
        .msg-call-overlay {
          position: absolute; inset: 0;
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          z-index: 100;
          animation: msgFadeIn 0.3s ease;
        }
        .msg-call-card {
          background: white;
          border-radius: 24px;
          padding: 40px 50px;
          text-align: center;
          display: flex; flex-direction: column; align-items: center; gap: 12px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .msg-call-avatar {
          width: 80px; height: 80px; border-radius: 50%;
          background: #0a84ff; color: white;
          display: flex; align-items: center; justify-content: center;
          font-size: 2rem; font-weight: 700;
        }
        .msg-call-name { font-size: 1.3rem; font-weight: 700; color: #050505; }
        .msg-call-status { font-size: 0.9rem; color: #65676b; }
        .msg-call-actions { display: flex; gap: 20px; margin-top: 12px; }
        .msg-call-mute, .msg-call-end {
          width: 56px; height: 56px; border-radius: 50%;
          border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.15s, transform 0.1s;
        }
        .msg-call-mute { background: #f0f2f5; color: #65676b; }
        .msg-call-mute:hover { background: #e4e6eb; }
        .msg-call-end { background: #e74c3c; color: white; }
        .msg-call-end:hover { background: #c0392b; }
        .msg-call-end:active { transform: scale(0.92); }

        /* Auth wall */
        .msg-auth-wall {
          padding: 2rem; text-align: center; color: #65676b;
          font-family: -apple-system, sans-serif;
        }

        /* ── Responsive ── */
        @media (max-width: 680px) {
          .msg-sidebar { width: 100%; position: absolute; z-index: 50; top: 0; left: 0; bottom: 0; }
          .msg-sidebar.msg-sidebar--hidden { transform: translateX(-100%); }
          .msg-back-btn { display: flex !important; }
          .msg-chat-view { position: absolute; inset: 0; background: #f0f2f5; z-index: 60; }
        }

        /* Scrollbar styling */
        .msg-conv-list::-webkit-scrollbar,
        .msg-messages-area::-webkit-scrollbar { width: 4px; }
        .msg-conv-list::-webkit-scrollbar-thumb,
        .msg-messages-area::-webkit-scrollbar-thumb { background: #d0d2d6; border-radius: 4px; }
        `;
        document.head.appendChild(style);
    }
}
